import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StandardActCodeForm } from "../components/StandardActCodeForm";
import { fetchAdminMedicalAct, updateAdminMedicalAct } from "../api/adminApi";
import { getAuthToken } from "../auth";
import {
  getStandardActCodeById,
  loadStandardActCodes,
  updateStandardActCode,
} from "../standardActCodesStorage";
import type { StandardActCode } from "../types";

type EditStandardActCodePageProps = {
  actId: string;
};

export function EditStandardActCodePage({ actId }: EditStandardActCodePageProps) {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.standardCodes.edit;
  const { showToast } = useToast();
  const [act, setAct] = useState<StandardActCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        if (getAuthToken()) {
          const apiAct = await fetchAdminMedicalAct(Number(actId));
          if (cancelled) return;
          setAct({
            id: String(apiAct.id),
            code: apiAct.code,
            label: apiAct.label || apiAct.name,
            category: apiAct.category || "",
            specialty: apiAct.specialty || "",
            description: apiAct.description || "",
          });
        } else {
          setAct(getStandardActCodeById(actId) ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger l'acte.");
          setAct(getStandardActCodeById(actId) ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [actId]);

  if (loading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  if (!act) {
    return (
      <div className="theme-card p-6 text-sm text-muted-foreground">
        {error ?? labels.notFound}
        <Link to="/dashboard/acts/standard-codes" className="ml-2 text-primary hover:underline">
          {t.safecheck.back}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.acts },
          { label: t.safecheck.acts.standardCodes.title, href: "/dashboard/acts/standard-codes" },
          { label: labels.title },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/acts/standard-codes"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {t.safecheck.back}
        </Link>
      </div>

      <div className="theme-card p-6">
        <div className="mb-6 flex items-center gap-2 text-base font-semibold text-foreground">
          <Pencil size={16} className="text-yellow" />
          {labels.cardTitle}
        </div>

        <StandardActCodeForm
          labels={labels}
          cancelHref="/dashboard/acts/standard-codes"
          initialValues={act}
          onSubmit={async (values) => {
            if (!getAuthToken()) {
              updateStandardActCode(loadStandardActCodes(), actId, values);
              showToast(t.actions.saved);
              return;
            }
            await updateAdminMedicalAct(Number(actId), {
              code: values.code,
              label: values.label,
              category: values.category,
              specialty: values.specialty,
              description: values.description,
            });
            showToast(t.actions.saved);
          }}
        />
      </div>
    </>
  );
}
